import React, { useState, useMemo } from 'react';
import Header from './Header';
import Footer from './Footer';

// ---------------------------------------------------------------------------
// Measured cost baselines (source: production billing cron + checkpoint validator)
// All per-minute rates are provider cost BEFORE markup.
// ---------------------------------------------------------------------------

// Measured production baselines: $0.14/min fully loaded (with 30% markup + telephony)
// Breakdown derived from billing cron actuals:
//   LLM (bot + orchestrator): ~$0.085/min provider cost
//   TTS: ~$0.004/min provider cost
//   STT: $0.0043/min (Deepgram Nova-2)
//   Telephony: $0.0085/min (Twilio PSTN inbound)
// Total provider: ~$0.10/min -> with 30% markup on AI = ~$0.14/min

const COST_BASELINES = {
  // Per-minute provider costs (before markup) by LLM provider
  'OpenAI GPT-4o-mini': { llm: 0.085, label: 'OpenAI GPT-4o-mini' },
  'Groq Llama 3.3 70B': { llm: 0.045, label: 'Groq Llama 3.3 70B' },  // ~47% cheaper LLM rates
};

const TTS_BASELINES = {
  'Deepgram Aura 2': { cost: 0.004, label: 'Deepgram Aura 2' },
  'OpenAI TTS-1': { cost: 0.010, label: 'OpenAI TTS-1' },       // ~2.5x Deepgram
  'Cartesia Sonic 2': { cost: 0.005, label: 'Cartesia Sonic 2' }, // slightly above Deepgram
};

const STT_RATE_PER_MIN = 0.0043;          // Deepgram Nova-2
const TWILIO_VOICE_RATE_PER_MIN = 0.0085; // Twilio PSTN inbound
const TWILIO_SMS_PER_MSG = 0.0079;        // Twilio SMS
const PLATFORM_MARKUP = 0.30;             // 30% markup on provider costs (AI + telephony)

// Infrastructure (from Cost Optimizer + billing cron)
const GATEWAY_FEE = 0.25;                 // per call on-demand
const INSTANCE_HOURLY = 0.10;             // per instance/hour
const AVG_SMS_PER_CALL = 0.5;             // avg SMS messages per call
const PEAK_FACTOR = 2.5;                  // peak concurrent = avg * 2.5x
const CONCURRENT_PER_INSTANCE = 10;       // max concurrent calls per instance (conservative)

// Fixed monthly (with provider markup baked in)
const REDIS_MONTHLY = 6.50;               // $5.00 × 1.30 markup
const PHONE_MONTHLY = 1.50;               // Twilio local number

// Daily call volume slider stops
const DAILY_VOLUME_STOPS = [5, 10, 20, 50, 100, 200, 500, 1000, 2000];

const DURATION_OPTIONS = [
  { label: '1 min', value: 1 },
  { label: '2 min', value: 2 },
  { label: '3 min', value: 3 },
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
];

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CostCalculator = ({ onNavigateLogin }) => {
  // Inputs
  const [volumeIdx, setVolumeIdx] = useState(3);         // 50 calls/day
  const [avgDuration, setAvgDuration] = useState(3);      // 3 min
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  const [bizDays, setBizDays] = useState(['mon', 'tue', 'wed', 'thu', 'fri']);
  const [pricingMode, setPricingMode] = useState('platform'); // 'platform' or 'byok'

  const llmProvider = 'OpenAI GPT-4o-mini';
  const ttsProvider = 'Deepgram Aura 2';

  const dailyCalls = DAILY_VOLUME_STOPS[volumeIdx];

  // ---------------------------------------------------------------------------
  // Calculations
  // ---------------------------------------------------------------------------

  const calc = useMemo(() => {
    const llm = COST_BASELINES[llmProvider];
    const tts = TTS_BASELINES[ttsProvider];

    // --- Derive monthly from daily volume + business schedule ---
    const bizHours = Math.max(1, endHour - startHour);
    const monthlyDays = Math.round(bizDays.length * 4.33);
    const monthlyCalls = dailyCalls * monthlyDays;
    const totalMinutes = monthlyCalls * avgDuration;

    // --- Per-minute provider costs (measured from production billing) ---
    const sttPerMin = STT_RATE_PER_MIN;
    const llmPerMin = llm.llm;
    const ttsPerMin = tts.cost;
    const telephonyPerMin = TWILIO_VOICE_RATE_PER_MIN;
    const smsPerCall = AVG_SMS_PER_CALL * TWILIO_SMS_PER_MSG;

    const providerPerMin = sttPerMin + llmPerMin + ttsPerMin + telephonyPerMin;
    const markup = pricingMode === 'platform' ? PLATFORM_MARKUP : 0;
    // In BYOK mode, all provider costs are billed directly to the developer's accounts
    const platformPerMin = pricingMode === 'byok' ? 0 : providerPerMin * (1 + markup);

    // --- Infrastructure costs (billing mode comparison) ---
    const callsPerHour = dailyCalls / bizHours;
    const avgConcurrent = (callsPerHour * avgDuration) / 60;
    const peakConcurrent = avgConcurrent * PEAK_FACTOR;
    // Minimum 2 instances for production (availability), scaling up with call volume
    const instancesNeeded = Math.max(2, Math.ceil(peakConcurrent / CONCURRENT_PER_INSTANCE));

    // Fixed infra: BYOK developers bring their own Twilio, so phone number is their cost
    const fixedInfra = pricingMode === 'byok' ? REDIS_MONTHLY : REDIS_MONTHLY + PHONE_MONTHLY;

    // On Demand
    const onDemandInfra = (monthlyCalls * GATEWAY_FEE) + fixedInfra;

    // Working Hours + On Demand after hours
    const instanceHrsMonth = bizHours * monthlyDays;
    const afterHoursCalls = Math.round(monthlyCalls * 0.05);
    const workingHrsInfra = (instanceHrsMonth * INSTANCE_HOURLY * instancesNeeded) + (afterHoursCalls * GATEWAY_FEE) + fixedInfra;

    // Always On
    const alwaysOnInfra = (730 * INSTANCE_HOURLY * instancesNeeded) + fixedInfra;

    // Pick cheapest
    let recommendedMode, recommendedLabel, recommendedColor, recommendedInstances, recommendedReason, infraCost;

    if (onDemandInfra <= workingHrsInfra && onDemandInfra <= alwaysOnInfra) {
      recommendedMode = 'on_demand';
      recommendedLabel = 'On Demand';
      recommendedColor = 'amber';
      recommendedInstances = 0;
      recommendedReason = 'Your call volume is low enough that paying per call ($0.25/call) is cheaper than running a dedicated instance.';
      infraCost = onDemandInfra;
    } else if (alwaysOnInfra <= workingHrsInfra) {
      recommendedMode = 'always_on';
      recommendedLabel = 'Always On';
      recommendedColor = 'green';
      recommendedInstances = instancesNeeded;
      recommendedReason = `At your volume, ${instancesNeeded > 1 ? `${instancesNeeded} dedicated instances` : 'a dedicated instance'} running 24/7 is cheaper than switching between hourly and per-call billing.`;
      infraCost = alwaysOnInfra;
    } else {
      recommendedMode = 'working_hours';
      recommendedLabel = 'Working Hours + On Demand';
      recommendedColor = 'blue';
      recommendedInstances = instancesNeeded;
      recommendedReason = `Run ${instancesNeeded} instance${instancesNeeded > 1 ? 's' : ''} during business hours, then switch to on-demand ($0.25/call) after hours to avoid paying for idle time.`;
      infraCost = workingHrsInfra;
    }

    // --- Totals ---
    const providerTotal = totalMinutes * platformPerMin;
    const smsTotal = pricingMode === 'byok' ? 0 : monthlyCalls * smsPerCall * (1 + markup);
    const monthlyTotal = providerTotal + smsTotal + infraCost;
    const costPerCall = monthlyCalls > 0 ? monthlyTotal / monthlyCalls : 0;

    // Savings (difference between most expensive and recommended infra)
    const maxInfra = Math.max(onDemandInfra, workingHrsInfra, alwaysOnInfra);
    const infraSavings = maxInfra - infraCost;

    return {
      // Per-minute breakdown
      sttPerMin, llmPerMin, ttsPerMin, telephonyPerMin, providerPerMin, platformPerMin,
      smsPerCall: pricingMode === 'byok' ? 0 : smsPerCall * (1 + markup),
      // Totals
      providerTotal, smsTotal, infraCost, monthlyTotal, costPerCall,
      monthlyCalls, totalMinutes,
      // Infrastructure comparison
      onDemandInfra, workingHrsInfra, alwaysOnInfra,
      recommendedMode, recommendedLabel, recommendedColor, recommendedInstances, recommendedReason,
      infraSavings,
      instancesNeeded, peakConcurrent: peakConcurrent.toFixed(1),
      markup,
    };
  }, [dailyCalls, avgDuration, startHour, endHour, bizDays, pricingMode]);

  const toggleDay = (day) => {
    setBizDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const colorMap = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-800' },
    green: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-800', badge: 'bg-green-100 text-green-800' },
  };
  const rc = colorMap[calc.recommendedColor];

  const fmt = (v) => v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
  const fmtK = (v) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v.toLocaleString();

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9fb]">
      <Header onNavigateLogin={onNavigateLogin} />

      <main className="flex-1 px-4 sm:px-8 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Page header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold font-['Montserrat'] text-[#222324] mb-3">
              Cost Calculator
            </h1>
            <p className="text-gray-600 text-lg font-['Inter'] max-w-2xl mx-auto">
              Estimate your monthly costs based on call volume, business hours, and provider preferences. All prices in USD.
            </p>
            <p className="text-gray-400 text-sm font-['Inter'] max-w-2xl mx-auto mt-2">
              These estimates are illustrative and intended to compare cost differences between billing options based on typical usage scenarios. Actual costs will vary based on call complexity, LLM token usage, and provider rates.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* ----------------------------------------------------------- */}
            {/* LEFT: Inputs                                                 */}
            {/* ----------------------------------------------------------- */}
            <div className="lg:col-span-5 flex flex-col gap-6">

              {/* Pricing mode toggle */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex">
                  <button
                    onClick={() => setPricingMode('platform')}
                    className={`flex-1 py-3 px-4 text-sm font-bold font-['Montserrat'] transition-colors ${
                      pricingMode === 'platform'
                        ? 'bg-gradient-to-br from-[#055EFD] to-[#0449d4] !text-white'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <i className={`fa fa-cloud text-sm ${pricingMode === 'platform' ? 'text-white' : 'text-gray-400'}`}></i>
                      <span>Platform Keys</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setPricingMode('byok')}
                    className={`flex-1 py-3 px-4 text-sm font-bold font-['Montserrat'] transition-colors ${
                      pricingMode === 'byok'
                        ? 'bg-gradient-to-br from-[#055EFD] to-[#0449d4] !text-white'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <i className={`fa fa-key text-sm ${pricingMode === 'byok' ? 'text-white' : 'text-gray-400'}`}></i>
                      <span>Bring Your Own Keys</span>
                    </div>
                  </button>
                </div>
                <div className="px-4 py-2 text-center">
                  <span className="text-xs font-['Inter'] text-gray-500">
                    {pricingMode === 'platform'
                      ? 'We manage providers. Your cost = provider rates + 30%'
                      : 'Use your own API keys. You only pay us for compute & platform.'}
                  </span>
                </div>
              </div>

              {/* Volume & Duration */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-4">Call Volume</h3>

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium font-['Inter'] text-gray-600">Daily Calls</label>
                    <span className="text-lg font-bold font-['Montserrat'] text-[#055EFD]">{fmtK(dailyCalls)}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={DAILY_VOLUME_STOPS.length - 1}
                    value={volumeIdx}
                    onChange={(e) => setVolumeIdx(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#055EFD]"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-gray-400 font-['Inter']">5</span>
                    <span className="text-[10px] text-gray-400 font-['Inter']">2,000</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 px-3 py-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="fa fa-calendar text-gray-400 text-xs"></i>
                      <span className="text-xs font-['Inter'] text-gray-600">
                        <span className="font-semibold text-[#222324]">{fmtK(calc.monthlyCalls)}</span> calls/mo
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fa fa-server text-gray-400 text-xs"></i>
                      <span className="text-xs font-['Inter'] text-gray-600">
                        <span className="font-semibold text-[#222324]">{calc.instancesNeeded}</span> instances
                        {calc.instancesNeeded === 2 && <span className="text-gray-400 ml-1">(min)</span>}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium font-['Inter'] text-gray-600 mb-2 block">Avg Call Duration</label>
                  <div className="flex gap-2">
                    {DURATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setAvgDuration(opt.value)}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold font-['Inter'] transition-colors ${
                          avgDuration === opt.value
                            ? 'bg-[#055EFD] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 font-['Inter'] mt-2">
                    Call duration primarily affects AI provider costs. Infrastructure costs scale with call volume, not duration.
                  </p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-4">Business Schedule</h3>

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium font-['Inter'] text-gray-600">Working Hours</label>
                    <span className="text-sm font-bold font-['Montserrat'] text-[#222324]">
                      {String(startHour).padStart(2, '0')}:00 - {String(endHour).padStart(2, '0')}:00
                      <span className="text-xs font-normal text-gray-400 ml-1">({Math.max(1, endHour - startHour)}h)</span>
                    </span>
                  </div>
                  <div className="relative h-8 mb-1">
                    {/* Track background */}
                    <div className="absolute top-3 left-0 right-0 h-2 bg-gray-200 rounded-lg" />
                    {/* Active range highlight */}
                    <div className="absolute top-3 h-2 bg-[#055EFD] rounded-lg opacity-30"
                      style={{ left: `${(startHour / 24) * 100}%`, right: `${100 - (endHour / 24) * 100}%` }} />
                    {/* Start handle */}
                    <input type="range" min="0" max="23" value={startHour}
                      onChange={(e) => { const v = parseInt(e.target.value); if (v < endHour) setStartHour(v); }}
                      className="absolute top-0 left-0 w-full h-8 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#055EFD] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#055EFD] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer" />
                    {/* End handle */}
                    <input type="range" min="1" max="24" value={endHour}
                      onChange={(e) => { const v = parseInt(e.target.value); if (v > startHour) setEndHour(v); }}
                      className="absolute top-0 left-0 w-full h-8 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#055EFD] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#055EFD] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer" />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 font-['Inter'] px-0.5">
                    <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>12am</span>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium font-['Inter'] text-gray-600 mb-2 block">Business Days</label>
                  <div className="flex gap-1.5">
                    {DAY_KEYS.map((day, i) => (
                      <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold font-['Inter'] transition-colors ${
                          bizDays.includes(day)
                            ? 'bg-[#055EFD] text-white'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {DAY_LABELS[i]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

            </div>

            {/* ----------------------------------------------------------- */}
            {/* RIGHT: Results                                               */}
            {/* ----------------------------------------------------------- */}
            <div className="lg:col-span-7 flex flex-col gap-6">

              {/* Competitive differentiator */}
              <div className="bg-gradient-to-r from-[#055EFD] to-[#0449d4] rounded-xl p-5 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa fa-bolt text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold font-['Montserrat'] mb-1">Zero Cold Starts. Dedicated Infrastructure. Pay-Per-Call Pricing.</div>
                    <p className="text-xs font-['Inter'] text-white/80 leading-relaxed">
                      Other platforms force you to choose: pay for always-on dedicated instances, or accept shared infrastructure with cold start delays.
                      Our gateway architecture answers every call instantly using your isolated data layer, while your dedicated container scales up in the background.
                      No caller ever waits. No data is ever shared.
                    </p>
                  </div>
                </div>
              </div>

              {/* Headline cost */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-xs font-medium font-['Inter'] text-gray-500 uppercase tracking-wider mb-1">
                      {pricingMode === 'byok' ? 'Estimated Platform Cost' : 'Estimated Monthly Cost'}
                    </div>
                    <div className="text-4xl font-bold font-['Montserrat'] text-[#222324]">
                      ${calc.monthlyTotal.toFixed(2)}
                      <span className="text-base font-normal text-gray-400 ml-1">/mo</span>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold font-['Montserrat'] text-[#055EFD]">{fmt(calc.costPerCall)}</div>
                      <div className="text-[10px] text-gray-500 font-['Inter']">per call{pricingMode === 'byok' ? ' (platform)' : ''}</div>
                    </div>
                    {pricingMode === 'platform' && (
                      <div className="text-center">
                        <div className="text-xl font-bold font-['Montserrat'] text-[#055EFD]">{fmt(calc.platformPerMin)}</div>
                        <div className="text-[10px] text-gray-500 font-['Inter']">per minute (AI)</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500 font-['Inter']">
                  <i className="fa fa-info-circle text-gray-400"></i>
                  <span>Based on {fmtK(dailyCalls)} calls/day x {avgDuration} min avg x {bizDays.length} days/wk = {fmtK(calc.totalMinutes)} min/mo</span>
                </div>
              </div>

              {/* Per-minute breakdown */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-4">Cost Per Minute Breakdown</h3>
                <div className="flex flex-col gap-2">
                  {/* AI providers group */}
                  {[
                    { label: 'STT (Deepgram Nova-2)', cost: calc.sttPerMin },
                    { label: `LLM (${llmProvider})`, cost: calc.llmPerMin },
                    { label: `TTS (${ttsProvider})`, cost: calc.ttsPerMin },
                  ].map((item) => {
                    const isByok = pricingMode === 'byok';
                    const displayCost = isByok ? item.cost : item.cost * (1 + PLATFORM_MARKUP);
                    return (
                      <div key={item.label} className={`flex items-center justify-between py-2 border-b border-gray-50 ${isByok ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          {isByok && <i className="fa fa-check-circle text-green-500 text-xs"></i>}
                          <span className={`text-sm font-['Inter'] ${isByok ? 'text-gray-400 line-through' : 'text-[#222324]'}`}>{item.label}</span>
                        </div>
                        <span className={`text-sm font-bold font-['Montserrat'] ${isByok ? 'text-green-600' : 'text-[#222324]'}`}>
                          {isByok ? 'your cost' : `$${displayCost.toFixed(4)}/min`}
                        </span>
                      </div>
                    );
                  })}

                  {/* Telephony group (Twilio: voice + SMS + phone number) */}
                  {(() => {
                    const isByok = pricingMode === 'byok';
                    const telephonyCost = calc.telephonyPerMin * (1 + PLATFORM_MARKUP);
                    return (
                      <div className={`py-2 border-b border-gray-50 ${isByok ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isByok && <i className="fa fa-check-circle text-green-500 text-xs"></i>}
                            <span className={`text-sm font-['Inter'] ${isByok ? 'text-gray-400 line-through' : 'text-[#222324]'}`}>Telephony (Twilio)</span>
                          </div>
                          <span className={`text-sm font-bold font-['Montserrat'] ${isByok ? 'text-green-600' : 'text-[#222324]'}`}>
                            {isByok ? 'your cost' : `$${telephonyCost.toFixed(4)}/min`}
                          </span>
                        </div>
                        <div className={`text-xs font-['Inter'] mt-1 ml-6 ${isByok ? 'text-gray-400' : 'text-gray-500'}`}>
                          {isByok
                            ? 'Voice, SMS, and phone number billed to your Twilio account'
                            : `Includes PSTN voice, ~${AVG_SMS_PER_CALL} SMS/call, and phone number`
                          }
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between pt-2 mt-1">
                    <span className="text-sm font-bold font-['Inter'] text-[#222324]">
                      Total cost per minute {calc.markup > 0 ? '(incl. 30%)' : ''}
                    </span>
                    <span className="text-sm font-bold font-['Montserrat'] text-[#055EFD]">${calc.platformPerMin.toFixed(4)}/min</span>
                  </div>
                </div>
              </div>

              {/* Monthly cost summary */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-4">Monthly Cost Summary</h3>
                <div className="flex flex-col gap-3">
                  {pricingMode === 'platform' ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium font-['Inter'] text-[#222324]">AI & Telephony</div>
                          <div className="text-xs text-gray-500 font-['Inter']">{fmtK(calc.totalMinutes)} min x ${calc.platformPerMin.toFixed(4)}/min</div>
                        </div>
                        <span className="text-sm font-bold font-['Montserrat'] text-[#222324]">${calc.providerTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium font-['Inter'] text-[#222324]">SMS Notifications</div>
                          <div className="text-xs text-gray-500 font-['Inter']">{fmtK(calc.monthlyCalls)} calls x ~{AVG_SMS_PER_CALL} SMS/call</div>
                        </div>
                        <span className="text-sm font-bold font-['Montserrat'] text-[#222324]">${calc.smsTotal.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium font-['Inter'] text-[#222324]">AI, Telephony & SMS</div>
                        <div className="text-xs text-gray-500 font-['Inter']">Billed directly to your Deepgram, OpenAI/Groq, and Twilio accounts</div>
                      </div>
                      <span className="text-sm font-bold font-['Inter'] text-green-600">your cost</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium font-['Inter'] text-[#222324] flex items-center gap-2">
                        Infrastructure
                        <a href="#infra-config" onClick={(e) => { e.preventDefault(); document.getElementById('infra-config')?.scrollIntoView({ behavior: 'smooth' }); }}
                          className="text-[10px] font-normal text-[#055EFD] hover:underline">see configuration below</a>
                      </div>
                      <div className="text-xs text-gray-500 font-['Inter']">
                        {calc.recommendedLabel} + single-tenant data isolation
                      </div>
                    </div>
                    <span className="text-sm font-bold font-['Montserrat'] text-[#222324]">${calc.infraCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-base font-bold font-['Montserrat'] text-[#222324]">
                      {pricingMode === 'byok' ? 'Platform Total' : 'Total'}
                    </span>
                    <span className="text-xl font-bold font-['Montserrat'] text-[#055EFD]">${calc.monthlyTotal.toFixed(2)}/mo</span>
                  </div>
                  {pricingMode === 'byok' && (
                    <div className="text-xs text-gray-500 font-['Inter'] flex items-center gap-1">
                      <i className="fa fa-info-circle text-gray-400"></i>
                      Plus provider costs billed directly to your Deepgram, OpenAI/Groq, and Twilio accounts
                    </div>
                  )}
                </div>
              </div>

              {/* Infrastructure recommendation */}
              <div id="infra-config" className={`rounded-xl border-2 ${rc.border} ${rc.bg} p-6`}>
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-3">Infrastructure Configuration</h3>
                <div className="flex items-start gap-3 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold font-['Inter'] ${rc.badge}`}>
                    {calc.recommendedLabel}
                  </span>
                  <span className="text-xs font-medium font-['Inter'] text-gray-500 mt-0.5">Recommended availability configuration</span>
                </div>
                <p className={`text-sm font-['Inter'] ${rc.text} mb-4`}>{calc.recommendedReason}</p>

                {/* 3-way comparison */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { mode: 'on_demand', label: 'On Demand', cost: calc.onDemandInfra, desc: `${fmtK(calc.monthlyCalls)} calls x $0.25` },
                    { mode: 'working_hours', label: 'Working Hrs', cost: calc.workingHrsInfra, desc: `Instance + after-hrs calls` },
                    { mode: 'always_on', label: 'Always On', cost: calc.alwaysOnInfra, desc: `${calc.instancesNeeded} inst x 730h x $0.10` },
                  ].map((opt) => {
                    const isRecommended = opt.mode === calc.recommendedMode;
                    return (
                      <div
                        key={opt.mode}
                        className={`rounded-lg p-3 text-center ${
                          isRecommended ? 'bg-white border-2 border-[#055EFD]' : 'bg-white/50 border border-gray-200'
                        }`}
                      >
                        {isRecommended && (
                          <div className="text-[10px] font-bold font-['Inter'] text-[#055EFD] uppercase tracking-wider mb-1">Best value</div>
                        )}
                        <div className={`text-xs font-bold font-['Montserrat'] mb-1 ${isRecommended ? 'text-[#222324]' : 'text-gray-400'}`}>{opt.label}</div>
                        <div className={`text-lg font-bold font-['Montserrat'] ${isRecommended ? 'text-[#055EFD]' : 'text-gray-400'}`}>${opt.cost.toFixed(0)}</div>
                        <div className="text-[10px] text-gray-400 font-['Inter'] mt-0.5">{opt.desc}</div>
                        <div className={`text-[10px] font-['Inter'] mt-1.5 flex items-center justify-center gap-1 ${isRecommended ? 'text-green-600' : 'text-gray-400'}`}>
                          <i className="fa fa-bolt text-[8px]"></i> No cold start latency
                        </div>
                      </div>
                    );
                  })}
                </div>

                {calc.infraSavings > 5 && (
                  <div className="mt-3 text-xs font-['Inter'] text-green-700 flex items-center gap-1">
                    <i className="fa fa-check-circle"></i>
                    Save ${calc.infraSavings.toFixed(0)}/mo vs most expensive option
                  </div>
                )}
              </div>

              {/* How it works - Gateway architecture */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-bold font-['Montserrat'] text-[#222324] mb-4">How We Eliminated the Cold Start Trade-off</h3>
                <div className="flex flex-col gap-4">
                  {/* The industry problem */}
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-xs font-bold font-['Inter'] text-red-800 uppercase tracking-wider mb-2">The industry trade-off</div>
                    <p className="text-sm font-['Inter'] text-red-700">
                      Voice AI containers take 60-90 seconds to cold start. Human conversation breaks at 500ms of silence.
                      Most platforms solve this with always-on shared infrastructure, sacrificing data isolation and charging premium rates.
                    </p>
                  </div>

                  {/* Our solution */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-xs font-bold font-['Inter'] text-green-800 uppercase tracking-wider mb-2">Our solution</div>
                    <div className="flex flex-col gap-3">
                      {[
                        { icon: 'fa-phone', title: 'Call arrives', desc: 'Gateway answers instantly using your isolated Redis and DynamoDB. Zero delay.' },
                        { icon: 'fa-server', title: 'Container scales up', desc: 'Your dedicated instance starts in the background while the call is already live.' },
                        { icon: 'fa-exchange', title: 'Seamless handoff', desc: 'Transfer happens at a natural conversational pause. The caller never notices.' },
                      ].map((step, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className={`fa ${step.icon} text-green-700 text-xs`}></i>
                          </div>
                          <div>
                            <div className="text-sm font-semibold font-['Inter'] text-green-900">{step.title}</div>
                            <div className="text-xs font-['Inter'] text-green-700">{step.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Key differentiators */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { icon: 'fa-lock', title: 'Full data isolation', desc: 'Dedicated Redis instances and DynamoDB tables per developer. Separate credentials, separate network paths.' },
                      { icon: 'fa-tachometer', title: 'Instant response', desc: 'Every call answered immediately, regardless of billing mode. No cold start penalty.' },
                      { icon: 'fa-line-chart', title: 'Pay only when calls come in', desc: 'Scale to zero between calls. No idle containers burning budget when your phone isn\'t ringing.' },
                    ].map((item, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mx-auto mb-2 border border-gray-200">
                          <i className={`fa ${item.icon} text-[#055EFD] text-sm`}></i>
                        </div>
                        <div className="text-xs font-bold font-['Montserrat'] text-[#222324] mb-1">{item.title}</div>
                        <div className="text-[11px] font-['Inter'] text-gray-500 leading-relaxed">{item.desc}</div>
                      </div>
                    ))}
                  </div>

                  <a
                    href="https://blog.remote-assistant.ai/blog/99-problems-cold-start"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-sm font-semibold font-['Inter'] text-[#055EFD] hover:text-[#0449d4] transition-colors"
                  >
                    Read the full technical deep-dive
                    <i className="fa fa-arrow-right text-xs"></i>
                  </a>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-sm font-medium font-['Inter'] text-gray-600 mb-3">
                  Start with a $15 prepaid balance. No contracts, no minimum commitment.
                </p>
                <button
                  onClick={() => onNavigateLogin(true)}
                  className="px-8 py-3 bg-[#055EFD] rounded-lg text-white text-sm font-bold font-['Inter'] hover:bg-[#0449d4] transition-colors"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CostCalculator;
